<template>
  <div class="relative" data-aos="fade-right">
    <div
      class="absolute inset-0 bg-gradient-to-r from-[#fe6d0d] to-[#ffb47c] rounded-2xl sm:rounded-3xl blur-xl opacity-30"
    ></div>
    
    <div class="relative glass-morphism-dark rounded-2xl sm:rounded-3xl p-6 sm:p-8 md:p-10">
      <h2 class="text-2xl sm:text-3xl font-bold text-[#f4f4f4] mb-6 sm:mb-8">
        {{ $t('contactPage.formTitle') }}
      </h2>

      <form ref="contactForm" @submit.prevent="handleSubmit" class="space-y-4 sm:space-y-6">
        
        <div class="group">
          <label for="name" class="block text-sm font-medium text-[#f4f4f4]/80 mb-2">
            {{ $t('contactPage.nameLabel') }}
          </label>
          <div class="relative">
            <input
              type="text"
              name="name"
              id="name"
              v-model="formData.name"
              :placeholder="$t('contactPage.namePlaceholder')"
              class="w-full px-3 sm:px-4 py-2.5 sm:py-3 bg-[#f4f4f4]/10 border border-[#f4f4f4]/20 rounded-lg sm:rounded-xl text-[#f4f4f4] placeholder-[#f4f4f4]/50 focus:outline-none focus:ring-2 focus:ring-[#fe6d0d] focus:border-transparent backdrop-blur-md transition-all duration-300 text-sm sm:text-base"
              required
            />
             <div class="absolute inset-x-0 -bottom-px h-px bg-gradient-to-r from-transparent via-[#fe6d0d] to-transparent opacity-0 group-focus-within:opacity-100 transition-opacity duration-300"></div>
          </div>
        </div>

        <div class="group">
          <label for="email" class="block text-sm font-medium text-[#f4f4f4]/80 mb-2">
            {{ $t('contactPage.emailLabel') }}
          </label>
          <div class="relative">
            <input
              type="email"
              name="email"
              id="email"
              v-model="formData.email"
              :placeholder="$t('contactPage.emailPlaceholder')"
              class="w-full px-3 sm:px-4 py-2.5 sm:py-3 bg-[#f4f4f4]/10 border border-[#f4f4f4]/20 rounded-lg sm:rounded-xl text-[#f4f4f4] placeholder-[#f4f4f4]/50 focus:outline-none focus:ring-2 focus:ring-[#fe6d0d] focus:border-transparent backdrop-blur-md transition-all duration-300 text-sm sm:text-base"
              required
            />
            <div class="absolute inset-x-0 -bottom-px h-px bg-gradient-to-r from-transparent via-[#fe6d0d] to-transparent opacity-0 group-focus-within:opacity-100 transition-opacity duration-300"></div>
          </div>
        </div>

        <div class="group">
          <label for="message" class="block text-sm font-medium text-[#f4f4f4]/80 mb-2">
            {{ $t('contactPage.messageLabel') }}
          </label>
          <div class="relative">
            <textarea
              id="message"
              name="message"
              rows="4"
              v-model="formData.message"
              :placeholder="$t('contactPage.messagePlaceholder')"
              class="w-full px-3 sm:px-4 py-2.5 sm:py-3 bg-[#f4f4f4]/10 border border-[#f4f4f4]/20 rounded-lg sm:rounded-xl text-[#f4f4f4] placeholder-[#f4f4f4]/50 focus:outline-none focus:ring-2 focus:ring-[#fe6d0d] focus:border-transparent backdrop-blur-md transition-all duration-300 resize-none text-sm sm:text-base"
              required
            ></textarea>
            <div class="absolute inset-x-0 -bottom-px h-px bg-gradient-to-r from-transparent via-[#fe6d0d] to-transparent opacity-0 group-focus-within:opacity-100 transition-opacity duration-300"></div>
          </div>
        </div>

        <div class="pt-2 sm:pt-4">
          <div id="recaptcha-container" class="mb-4"></div>

          <button
            type="submit"
            :disabled="isLoading"
            class="group relative w-full flex justify-center items-center gap-2 py-3 sm:py-4 px-6 sm:px-8 bg-gradient-to-r from-[#fe6d0d] to-[#ffb47c] text-[#f4f4f4] font-bold rounded-lg sm:rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:scale-[1.02] disabled:opacity-70 disabled:cursor-not-allowed"
          >
            <span class="relative z-10 flex items-center gap-2">
              {{ isLoading ? $t('contactPage.sending') : $t('contactPage.submitButton') }}
              <ArrowRightIcon
                v-if="!isLoading"
                class="w-4 h-4 sm:w-5 sm:h-5 transform group-hover:translate-x-1 transition-transform"
              />
            </span>
          </button>
          
          <p v-if="submitStatus === 'success'" class="mt-3 text-green-400 text-sm text-center">{{ $t('contactPage.successMessage') }}</p>
          <p v-if="submitStatus === 'error'" class="mt-3 text-red-400 text-sm text-center">{{ $t('contactPage.errorMessage') }}</p>
          <p v-if="submitStatus === 'captcha_error'" class="mt-3 text-yellow-400 text-sm text-center"> {{ $t('contactPage.captchaErrorMessage') }}</p>
        </div>
      </form>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, reactive } from 'vue'
import { ArrowRightIcon } from 'lucide-vue-next'

const siteKey = import.meta.env.VITE_RECAPTCHA_SITE_KEY
const apiBaseUrl = import.meta.env.VITE_API_URL

const formData = reactive({
  name: '',
  email: '',
  message: ''
})

const isLoading = ref(false)
const submitStatus = ref<'' | 'success' | 'error' | 'captcha_error'>('')

interface ReCaptcha {
  render?: (container: string | HTMLElement, parameters: { sitekey: string; theme: string }) => number;
  getResponse: (widgetId?: number) => string;
  reset: (widgetId?: number) => void;
}

declare global {
  interface Window {
    grecaptcha?: ReCaptcha;
  }
}

let widgetId: number | null = null

onMounted(() => {
  if (window.grecaptcha && window.grecaptcha.render) {
    renderCaptcha()
  } else {
    const checkCaptcha = setInterval(() => {
      if (window.grecaptcha && window.grecaptcha.render) {
        clearInterval(checkCaptcha)
        renderCaptcha()
      }
    }, 100)
  }
})

const renderCaptcha = () => {
  try {
    if (document.getElementById('recaptcha-container') && window.grecaptcha?.render) {
      widgetId = window.grecaptcha.render('recaptcha-container', {
        'sitekey': siteKey,
        'theme': 'dark'
      })
    }
  } catch (e) {
    console.error('Captcha render error:', e)
  }
}

const handleSubmit = async () => {
  submitStatus.value = ''
  
  let token = '';
  try {
    if (window.grecaptcha) {
      token = widgetId !== null
        ? window.grecaptcha.getResponse(widgetId)
        : window.grecaptcha.getResponse();
    }
  } catch (e) {
    console.error("Captcha alınırken hata oluştu:", e);
  }

  console.log("Gönderilen Token:", token);

  if (!token) {
    submitStatus.value = 'captcha_error'
    return
  }

  isLoading.value = true

  try {
    const response = await fetch(`${apiBaseUrl}/send-mail`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        name: formData.name,
        email: formData.email,
        message: formData.message,
        source: 'corporate-website',
        captcha: token
      })
    })

    const contentType = response.headers.get('content-type')
    if (contentType && contentType.includes('application/json')) {
      const result = await response.json()
      console.log("PHP Yanıtı:", result);

      if (response.ok && result.success) {
        submitStatus.value = 'success'
        formData.name = ''
        formData.email = ''
        formData.message = ''
        if (widgetId !== null && window.grecaptcha?.reset) {
          window.grecaptcha.reset(widgetId)
        }
      } else {
        submitStatus.value = 'error'
      }
    } else {
      const text = await response.text()
      console.error('Backend HTML döndürdü:', text.substring(0, 500))
      submitStatus.value = 'error'
    }

  } catch (error) {
    console.error('Network Hatası:', error)
    submitStatus.value = 'error'
  } finally {
    isLoading.value = false
  }
}
</script>

<style scoped>
.glass-morphism-dark {
  background: rgba(244, 244, 244, 0.05);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(244, 244, 244, 0.1);
}
</style>